import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';

import { analytics } from 'analytics';
import { useApiContext } from 'contexts/apiContext';

import { AUTOSAVE_DEBOUNCE_MS } from '../constants';
import { useDiagramCache } from './caching';
import { formToSankey, hasModifiedForm, sankeyToForm } from './form';
import { useAuthorizationCheck, useCurrentRouteSankeyId } from './routes';
import { useCurrentUser } from './user';

/*
 * This hook calls the callback function with a debounce whenever the form data changes.
 */
export function useSankeyFormSyncer(
  form: SankeyForm,
  callback: () => void,
  debounceMs: number
) {
  const values = useWatch(form);
  const debouncedCallback = useDebounceCallback(callback, debounceMs);

  useEffect(() => {
    debouncedCallback();
    return () => {
      debouncedCallback.cancel();
    };
  }, [values]);
}

/*
 * This hook is used to load the current Sankey diagram data from the server or
 * from the cache, depending on the user's permissions and the availability of
 * the cached version. It is also called when returning focus on the browser tab
 * to ensure the latest version of the Sankey diagram is loaded.
 */
export const useDiagramLoader = (form: SankeyForm) => {
  const { api } = useApiContext();
  const { user } = useCurrentUser();
  const checkIfAuthorized = useAuthorizationCheck();
  const sankeyId = useCurrentRouteSankeyId();
  const cache = useDiagramCache(sankeyId);

  return useQuery({
    queryKey: ['sankey', sankeyId],
    queryFn: async () => {
      try {
        // If the user can't update diagrams but has a cached version,
        // load it from the cache instead of fetching from the server.
        if (!user?.can_update_sankeys && cache.load(form)) {
          return formToSankey(form);
        }
        // Otherwise the user can either update diagrams in which case their latest
        // version is already stored on the server or there is no cached version
        // available, so we need to fetch the latest version from the server either way.
        const { data: sankey } = await api().sankeys.retrieve({
          id: sankeyId,
        });
        form.reset(sankeyToForm(sankey));
        return sankey;
      } catch (error) {
        checkIfAuthorized(error, 'Error fetching current Sankey');
        return null; // Return null to stop fetch retries and not to propagate the error
      }
    },
    // Keep showing current Sankey data while refetching for smoother UI transitions.
    keepPreviousData: true,
    // Don't cache the current Sankey data response to avoid showing stale data.
    cacheTime: 0,
  });
};

/*
 * This hook is used to save the current Sankey diagram data to the server
 * and to the local cache. It also handles autosaving and error reporting.
 */
export const useDiagramSaver = (form: SankeyForm) => {
  const { api } = useApiContext();
  const { user } = useCurrentUser();
  const sankeyId = useCurrentRouteSankeyId();
  const cache = useDiagramCache(sankeyId);

  const [lastSaveTimestamp, setLastSaveTimestamp] = useState<string>();
  const [lastSaveError, setLastSaveError] = useState<string>();

  // Reset autosave state when the current diagram changes
  // TODO: Get rid of this once we know how to get the timestamp from the diagram itself
  useEffect(() => {
    setLastSaveTimestamp(undefined);
    setLastSaveError(undefined);
  }, [sankeyId]);

  const saveMutation = useMutation(async () => {
    try {
      // Always cache the diagram locally
      cache.save(form);
      if (user?.can_update_sankeys) {
        // In addition, save the diagram to the server if the user has permissions.
        await api().sankeys.partialUpdate({
          id: sankeyId,
          patchedSankeyRequest: formToSankey(form),
        });
        // If the form is valid and the API call succeeds, update the
        // timestamp and clear any previous error messages.
        setLastSaveTimestamp(new Date().toISOString());
        setLastSaveError(undefined);
      }
    } catch (e: any) {
      // If the form is valid but the save fails, show verbose
      // response to the user and report the error to Sentry.
      const statusCode = e?.response?.status ?? 0;
      setLastSaveError(
        e?.response?.data && statusCode >= 400 && statusCode < 500
          ? JSON.stringify(e.response.data)
          : 'An unexpected error occurred, please try reloading the page.'
      );
      analytics.reportError('Diagram autosave error', { error: e }, false);
    }
  });

  // Save the diagram with a debounce whenever the form data changes.
  useSankeyFormSyncer(
    form,
    () => {
      if (hasModifiedForm(form)) {
        form.handleSubmit(
          () => saveMutation.mutateAsync(),
          (errors) => {
            // If the form is invalid show an error message to the user.
            // TODO: Prevent this case altogether by validating individual input
            // elements and keeping the form valid at all times.
            setLastSaveError('Please fix the highlighted errors');
            analytics.reportError(
              'Diagram form validation error',
              { errors: JSON.stringify(errors), values: form.getValues() },
              false
            );
          }
        )();
      }
    },
    AUTOSAVE_DEBOUNCE_MS
  );

  return { lastSaveError, lastSaveTimestamp, ...saveMutation };
};
